import { SampleFile } from "projen";
import { AstroProject, AstroProjectOptions } from "./astro-project";
import { MONOREPO_LAYOUT } from "./monorepo-layout";
import { AstroIntegrationSpec } from "../astro/astro-config-options";
import { TurboRepo } from "../turbo";
import { VERSION } from "../versions";

/**
 * Role a {@link StarlightProject} plays inside the monorepo (ADR-006).
 *
 * - `"docs"` — the monorepo-wide Starlight docs site. Singleton: at most one
 *   docs-role project may exist per `MonorepoProject`. Defaults `outdir` to
 *   `docs/`.
 * - `"site"` — a user-facing Starlight site deployed from `sites/`. Behaves
 *   like a regular {@link AstroProject} for placement: defaults `outdir` to
 *   `sites/<scope>/<name>`.
 */
export const STARLIGHT_ROLE = {
  DOCS: "docs",
  SITE: "site",
} as const;

export type StarlightRole =
  (typeof STARLIGHT_ROLE)[keyof typeof STARLIGHT_ROLE];

/**
 * A single Starlight social icon link.
 */
export interface StarlightSocialLink {
  readonly icon: string;
  readonly label: string;
  readonly href: string;
}

/**
 * Starlight logo configuration.
 */
export interface StarlightLogo {
  readonly src: string;
  readonly alt?: string;
}

/**
 * Starlight edit-link configuration.
 */
export interface StarlightEditLink {
  readonly baseUrl: string;
}

/**
 * Sidebar item accepted by Starlight's `sidebar` config.
 *
 * Covers the shapes Starlight 0.39+ accepts:
 * - link (`{ label, link, badge? }`)
 * - group with nested items (`{ label, collapsed?, items }`)
 * - bare autogenerated entry, nested inside a group's `items` array
 *   (`{ autogenerate: { directory, collapsed? } }`)
 * - internal slug reference (`{ label, slug }`)
 *
 * Plus one legacy shape kept for one deprecation cycle:
 * - top-level autogenerated group (`{ label, collapsed?, autogenerate }`).
 *   Starlight v0.39.0 removed runtime support for this shape; the
 *   `StarlightProject` emitter translates it to the nested form
 *   `{ label, collapsed?, items: [{ autogenerate }] }` before writing
 *   `astro.config.mjs`. Migrate to the nested form directly — this
 *   variant will be removed in a future release.
 */
export type StarlightSidebarItem =
  | {
      readonly label: string;
      readonly link: string;
      readonly badge?:
        string | { readonly text: string; readonly variant?: string };
    }
  | {
      readonly label: string;
      readonly collapsed?: boolean;
      readonly items: ReadonlyArray<StarlightSidebarItem>;
    }
  | {
      readonly autogenerate: {
        readonly directory: string;
        readonly collapsed?: boolean;
      };
    }
  | {
      /** @deprecated Use `{ label, items: [{ autogenerate }] }` instead. Translated by the emitter for one cycle. */
      readonly label: string;
      readonly collapsed?: boolean;
      readonly autogenerate: {
        readonly directory: string;
        readonly collapsed?: boolean;
      };
    }
  | { readonly label: string; readonly slug: string };

/**
 * Configuration options for StarlightProject.
 */
export interface StarlightProjectOptions extends AstroProjectOptions {
  /**
   * Starlight site title (required).
   */
  readonly starlightTitle: string;

  /**
   * Role this Starlight project plays in the monorepo (ADR-006).
   *
   * - `"docs"` (default) — the monorepo-wide docs singleton. `outdir`
   *   defaults to `docs/`. Only one docs-role `StarlightProject` may
   *   exist per `MonorepoProject`; a second one fails synth.
   * - `"site"` — a regular user-facing Starlight site. `outdir`
   *   defaults to `sites/<scope>/<name>` (same rule as
   *   `AstroProject`). Multiple site-role projects are allowed.
   *
   * @default "docs"
   */
  readonly role?: StarlightRole;

  /**
   * Starlight site description.
   */
  readonly starlightDescription?: string;

  /**
   * Social links rendered in the Starlight header.
   */
  readonly social?: ReadonlyArray<StarlightSocialLink>;

  /**
   * Sidebar config (passed through verbatim). When omitted, Starlight
   * autogenerates the sidebar from the `src/content/docs/` tree.
   */
  readonly sidebar?: ReadonlyArray<StarlightSidebarItem>;

  /**
   * Custom CSS files loaded by Starlight.
   */
  readonly customCss?: ReadonlyArray<string>;

  /**
   * Site logo displayed in the Starlight header.
   */
  readonly logo?: StarlightLogo;

  /**
   * "Edit this page" link configuration.
   */
  readonly editLink?: StarlightEditLink;

  /**
   * `@astrojs/starlight` package version.
   *
   * @default VERSION.STARLIGHT_VERSION
   */
  readonly starlightVersion?: string;

  /**
   * sharp package version (required peer for Starlight's image pipeline).
   *
   * @default VERSION.SHARP_VERSION
   */
  readonly sharpVersion?: string;

  /**
   * Emit the sample Starlight landing page (`src/content/docs/index.mdx`).
   *
   * Does not gate `src/content.config.ts` — that file declares the `docs`
   * collection and is always emitted, since Starlight's content layer is
   * non-functional without it.
   *
   * @default false
   */
  readonly sampleContent?: boolean;
}

/**
 * Docs site scaffolded on top of {@link AstroProject} using the
 * Starlight Astro integration.
 */
export class StarlightProject extends AstroProject {
  /**
   * Role this Starlight project plays in the monorepo (ADR-006).
   *
   * - `"docs"` — monorepo-wide docs singleton at `/docs`.
   * - `"site"` — user-facing Starlight site under `sites/<scope>/<name>`.
   */
  public readonly role: StarlightRole;

  constructor(userOptions: StarlightProjectOptions) {
    const role: StarlightRole = userOptions.role ?? STARLIGHT_ROLE.DOCS;

    const starlightConfig = buildStarlightConfig(userOptions);
    const starlightSpec: AstroIntegrationSpec = {
      name: "starlight",
      importPath: "@astrojs/starlight",
      defaultImport: true,
      args: JSON.stringify(starlightConfig, null, 2),
    };

    /*************************************************************************
     *
     * Resolve outdir
     *
     * Docs-role projects default to `docs/` (ADR-006 docs singleton).
     * Site-role projects fall through to `AstroProject`'s own default —
     * `sites/<scope>/<name>` — so we leave `outdir` undefined for them
     * and let AstroProject resolve it from the scoped package name.
     *
     ************************************************************************/

    const resolvedOutdir =
      userOptions.outdir ??
      (role === STARLIGHT_ROLE.DOCS ? MONOREPO_LAYOUT.DOCS : undefined);

    /**
     * Prepend the Starlight integration so it appears first in the
     * rendered `integrations` array (matches Starlight docs convention).
     */
    /**
     * Exclude Starlight and sharp from projen's per-package ncu upgrade
     * workflow — same pattern AstroProject uses for `astro` — so
     * VERSION.STARLIGHT_VERSION / VERSION.SHARP_VERSION remain the single
     * source of truth.
     */
    const mergedOptions: AstroProjectOptions = {
      ...userOptions,
      outdir: resolvedOutdir,
      integrations: [starlightSpec, ...(userOptions.integrations ?? [])],
      depsUpgradeOptions: {
        ...userOptions.depsUpgradeOptions,
        exclude: [
          ...(userOptions.depsUpgradeOptions?.exclude ?? []),
          "@astrojs/starlight",
          "sharp",
        ],
      },
    };

    super(mergedOptions);

    this.role = role;

    const starlightVersion =
      userOptions.starlightVersion ?? VERSION.STARLIGHT_VERSION;
    const sharpVersion = userOptions.sharpVersion ?? VERSION.SHARP_VERSION;

    this.addDeps(
      `@astrojs/starlight@${starlightVersion}`,
      `sharp@${sharpVersion}`,
    );

    /**
     * Extend turbo compile inputs with Starlight's content collection tree.
     */
    const turbo = TurboRepo.of(this);
    if (turbo?.compileTask) {
      turbo.compileTask.inputs.push("src/content/**");
    }

    /**
     * Required content config. Starlight's content layer resolves every page
     * through the `docs` collection declared here, so a site without this file
     * builds only `404.html` — a warning, never an error. Emitted
     * unconditionally, and as a `SampleFile` (write-if-missing) because the
     * config is user-space: consumers extend the schema or add collections and
     * must never have those edits overwritten on regen.
     */
    new SampleFile(this, "src/content.config.ts", {
      contents: DEFAULT_CONTENT_CONFIG_TS,
    });

    /**
     * Optional sample landing page.
     */
    if (userOptions.sampleContent === true) {
      new SampleFile(this, "src/content/docs/index.mdx", {
        contents: DEFAULT_INDEX_MDX,
      });
    }
  }
}

/**
 * Build the JSON-serializable Starlight config object from user options.
 */
function buildStarlightConfig(
  options: StarlightProjectOptions,
): Record<string, unknown> {
  const config: Record<string, unknown> = {
    title: options.starlightTitle,
  };
  if (options.starlightDescription !== undefined) {
    config.description = options.starlightDescription;
  }
  if (options.social !== undefined) {
    config.social = options.social;
  }
  if (options.sidebar !== undefined) {
    config.sidebar = options.sidebar.map(translateSidebarItem);
  }
  if (options.customCss !== undefined) {
    config.customCss = options.customCss;
  }
  if (options.logo !== undefined) {
    config.logo = options.logo;
  }
  if (options.editLink !== undefined) {
    config.editLink = options.editLink;
  }
  return config;
}

/**
 * Translate a single sidebar item from the legacy top-level
 * `{ label, collapsed?, autogenerate }` shape to the Starlight 0.39+
 * nested form `{ label, collapsed?, items: [{ autogenerate }] }`.
 *
 * Recurses through any `items` array so nested groups are translated
 * at any depth. Items that are not in the legacy shape are returned
 * unchanged (apart from a recursive walk of their `items`, when present).
 */
function translateSidebarItem(item: StarlightSidebarItem): unknown {
  if (item === null || typeof item !== "object") {
    return item;
  }

  const record = item as Record<string, unknown>;

  /**
   * Legacy shape: a group with both a top-level `label` and a top-level
   * `autogenerate` field. Rewrite to the nested-items form.
   */
  if (
    typeof record.label === "string" &&
    record.autogenerate !== undefined &&
    record.items === undefined
  ) {
    const translated: Record<string, unknown> = {
      label: record.label,
      items: [{ autogenerate: record.autogenerate }],
    };
    if (record.collapsed !== undefined) {
      translated.collapsed = record.collapsed;
    }
    return translated;
  }

  /**
   * Group with nested `items` — recurse so legacy shapes nested inside
   * a parent group are also translated.
   */
  if (Array.isArray(record.items)) {
    return {
      ...record,
      items: (record.items as ReadonlyArray<StarlightSidebarItem>).map(
        translateSidebarItem,
      ),
    };
  }

  return item;
}

const DEFAULT_INDEX_MDX = `---
title: Welcome
description: Starlight-powered documentation site.
---

# Hello, Starlight!

Edit \`src/content/docs/index.mdx\` to replace this page.
`;

const DEFAULT_CONTENT_CONFIG_TS = `import { defineCollection } from "astro:content";
import { docsLoader } from "@astrojs/starlight/loaders";
import { docsSchema } from "@astrojs/starlight/schema";

export const collections = {
  docs: defineCollection({ loader: docsLoader(), schema: docsSchema() }),
};
`;
